/* screens/academy/StudentDetailDrawer.jsx — карточка ученика (дровер 2-го уровня).
   Дрилл-ин из вкладки «Ученики»: GET /manage/enrollments/<pk>/detail/ (#13).
   Контракт — work/academy-feedback/contracts/student-detail.json (S13: читаем ровно эти ключи). */
import React from 'react';
import { Drawer } from '../../components/ui/Drawer.jsx';
import { QueryBoundary } from '../../components/patterns/States.jsx';
import { Avatar } from '../../components/ui/Avatar.jsx';
import { Pill } from '../../components/ui/Badge.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Field, Textarea } from '../../components/ui/Field.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { useConfirm } from '../../app/providers/ConfirmProvider.jsx';
import { useAuth } from '../../app/providers/AuthProvider.jsx';
import { hooks } from '../../hooks/index.jsx';
import { fmt } from '../../lib/format.js';
import * as api from '../../lib/api/academy.js';
import { RetakeQueue, useQuizReset } from './RetakeRequests.jsx';
import { ACCESS_TZ } from '../../lib/academy/access.js';

const { useFakeQuery } = hooks;

// status урока → подпись + тон бейджа (контракт: not_started|in_progress|completed)
const LESSON_STATUS = {
  completed: { label: 'Пройден', tone: 'ok', icon: 'check-circle' },
  in_progress: { label: 'В процессе', tone: 'info', icon: 'clock' },
  not_started: { label: 'Не начат', tone: 'neutral', icon: 'circle' },
};
function lessonStatusMeta(status) {
  return LESSON_STATUS[status] || LESSON_STATUS.not_started;
}

// status ДЗ → подпись + тон (контракт: submitted|accepted|revision|rejected)
const HW_STATUS = {
  submitted: { label: 'ДЗ на проверке', tone: 'info' },
  accepted: { label: 'ДЗ принято', tone: 'ok' },
  revision: { label: 'ДЗ на доработке', tone: 'warn' },
  rejected: { label: 'ДЗ отклонено', tone: 'bad' },
};
function hwStatusMeta(status) {
  return HW_STATUS[status] || null;
}

// status записи на курс → подпись + тон
const ENR_STATUS = {
  active: { label: 'Активна', tone: 'info' },
  completed: { label: 'Завершён', tone: 'ok' },
  expired: { label: 'Истёк', tone: 'neutral' },
};
function enrStatusMeta(status) {
  return ENR_STATUS[status] || { label: status || '—', tone: 'neutral' };
}

/* PL-8. Сертификат записи: отзыв и перевыпуск.

   Бэк (CertificateManageView + services.revoke_certificate/reissue_certificate)
   существовал и был покрыт тестами, а действия методисту были недоступны: отозвать
   ошибочно выданный документ или выпустить его заново можно было только через SQL.

   Причина ОБЯЗАТЕЛЬНА у обоих действий. У отзыва её требует бэк
   (code='reason_required'): аннулирование без объяснения ученику нечем оспорить.
   У перевыпуска — требуем мы: прошлый выпуск уезжает в history с этой причиной,
   и «перевыпущен непонятно почему» ничем не лучше. Форма — та же схема, что у
   переоткрытия проверки в ReviewDrawer: раскрывающееся поле, а не тихая кнопка. */
function CertificateSection({ enrollmentId, cert, onChanged, canManage }) {
  const toast = useToast();
  const [mode, setMode] = React.useState(null); // null | 'revoke' | 'reissue'
  const [reason, setReason] = React.useState('');
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  if (!cert) return null;

  const revoked = !!cert.revoked_at;
  const url = api.certificateUrl(cert.public_id);
  const close = () => { setMode(null); setReason(''); setError(''); };

  const submit = async () => {
    const r = reason.trim();
    if (!r) {
      setError(mode === 'revoke'
        ? 'Укажите причину аннулирования — её увидит ученик'
        : 'Укажите причину перевыпуска — она уйдёт в историю сертификата');
      return;
    }
    setBusy(true);
    try {
      if (mode === 'revoke') {
        await api.revokeCertificate(enrollmentId, r);
        toast.show('Сертификат отозван — публичная ссылка больше не открывается', 'muted');
      } else {
        await api.reissueCertificate(enrollmentId, r);
        toast.show('Сертификат перевыпущен — у него новый номер и новая ссылка', 'success');
      }
      close();
      onChanged();
    } catch (e) {
      toast.show(e?.detail || e?.message || 'Не удалось выполнить действие', 'danger');
    } finally { setBusy(false); }
  };

  return (
    <section className="acad-section">
      <div className="acad-section__head">
        {/* Иконка — только из реестра Icon.jsx: «award» там нет, и она молча
            отрисовалась бы пустотой. */}
        <Icon name="graduation-cap" size={15} /><span className="acad-section__title">Сертификат</span>
      </div>
      <div className="acad-student-card__meta">
        <span>
          <Pill tone={revoked ? 'bad' : 'ok'} size="sm" glyph={false}>
            {revoked ? 'Отозван' : 'Действителен'}</Pill>
        </span>
        <span className="t-caption text-mute">№ <span className="num">{cert.serial}</span></span>
        <span className="t-caption text-mute">Выдан: {fmt.date(cert.issued_at)}</span>
        {revoked && (
          <span className="t-caption text-mute">Отозван: {fmt.date(cert.revoked_at)}</span>
        )}
        {cert.reissue_count > 0 && (
          <span className="t-caption text-mute">Перевыпусков: <span className="num">{cert.reissue_count}</span></span>
        )}
      </div>
      {/* Причина отзыва — часть статуса, а не деталь журнала: без неё «Отозван»
          ничем не помогает ни ученику, ни следующему методисту. */}
      {revoked && cert.revoked_reason && (
        <div className="acad-notice acad-notice--warn" role="status">
          <Icon name="alert-triangle" size={15} />
          <span>Причина отзыва: {cert.revoked_reason}</span>
        </div>
      )}
      {!revoked && (
        <div className="cluster cluster-8" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <code className="t-caption" style={{ wordBreak: 'break-all' }}>{url}</code>
          <Button size="sm" variant="ghost" iconLeft="external-link"
            onClick={() => window.open(url, '_blank', 'noopener')}>Открыть</Button>
        </div>
      )}

      {/* Куратор видит статус, номер, ссылку и причину отзыва — это его
          разговор с учеником. Действия — только методисту: бэк за ними
          методистский, и кнопка без права была бы 403 по клику. */}
      {!canManage ? null : mode ? (
        <div className="acad-reopen stack stack-4">
          <Field label={mode === 'revoke' ? 'Причина аннулирования' : 'Причина перевыпуска'}
            error={error}
            hint={mode === 'revoke'
              ? 'Публичная ссылка перестанет открываться. Причина остаётся в сертификате — её видно в карточке ученика.'
              : 'Прошлый выпуск уйдёт в историю, сертификат получит НОВЫЙ номер и новую ссылку: ранее разосланная ссылка перестанет открываться.'}>
            <Textarea value={reason} minRows={2} invalid={!!error}
              onChange={(e) => { setReason(e.target.value); if (error) setError(''); }}
              placeholder={mode === 'revoke'
                ? 'Например: выдан по ошибке — курс не был завершён'
                : 'Например: в имени выпускника была опечатка'} />
          </Field>
          <div className="cluster cluster-8">
            <Button variant={mode === 'revoke' ? 'danger' : 'primary'} size="sm"
              loading={busy} onClick={submit}>
              {mode === 'revoke' ? 'Отозвать сертификат' : 'Перевыпустить'}</Button>
            <Button variant="ghost" size="sm" onClick={close}>Отмена</Button>
          </div>
        </div>
      ) : (
        <div className="cluster cluster-8" style={{ flexWrap: 'wrap' }}>
          {!revoked && (
            <Button variant="secondary" size="sm" iconLeft="trash"
              onClick={() => setMode('revoke')}>Отозвать</Button>
          )}
          <Button variant="secondary" size="sm" iconLeft="refresh"
            onClick={() => setMode('reissue')}>Перевыпустить</Button>
          <span className="t-caption text-mute">
            {revoked
              ? 'Перевыпуск вернёт ученику действующий сертификат под новым номером.'
              : 'Отзыв закрывает публичную ссылку; перевыпуск заменяет номер и ссылку.'}
          </span>
        </div>
      )}
    </section>
  );
}

/* Ручной зачёт урока: строка-форма под уроком.

   Причина ОБЯЗАТЕЛЬНА — как у отзыва сертификата и у вердикта «на доработку».
   Зачёт меняет учебный результат живого человека в обход требований урока, и
   через месяц «почему у него стоит пройдено» должно отвечаться карточкой, а не
   памятью методиста.

   Зачёт последнего незакрытого урока на курсе с сертификатом бэк отдаёт кодом
   `will_issue_certificate` ДО записи: показываем предупреждение и повторяем с
   force — узнать о выданном документе из письма ученику не то же самое, что
   решить это осознанно. */
function LessonCreditForm({ enrollmentId, lesson, onDone, onCancel }) {
  const toast = useToast();
  const [note, setNote] = React.useState('');
  const [error, setError] = React.useState('');
  const [warning, setWarning] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const submit = async (force) => {
    const text = note.trim();
    if (!text) { setError('Укажите причину — её увидит следующий методист'); return; }
    setBusy(true);
    try {
      const res = await api.setLessonCompletion(enrollmentId, lesson.id,
        { completed: true, note: text, force });
      toast.show('Урок зачтён', 'success');
      onDone(res);
    } catch (e) {
      if (api.isWillIssueCertGuard(e)) {
        setWarning(e.detail || 'Курс станет пройденным, ученику выдастся сертификат.');
      } else {
        setError(e?.detail || e?.message || 'Не удалось зачесть урок');
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="acad-reopen stack stack-4">
      <Field label={`Зачесть урок «${lesson.title}» вручную`} error={error}
        hint={'Требования урока (тест, ДЗ) при этом не проверяются, а следующие уроки, '
              + 'закрытые последовательностью, откроются. Причина останется в карточке ученика.'}>
        <Textarea value={note} minRows={2} invalid={!!error}
          onChange={(e) => { setNote(e.target.value); if (error) setError(''); }}
          placeholder="Например: прошёл очный тренинг 2 сентября, зачёт принял шеф" />
      </Field>
      {warning && (
        <div className="acad-notice acad-notice--warn" role="status">
          <Icon name="alert-triangle" size={15} />
          <span>{warning}</span>
        </div>
      )}
      <div className="cluster cluster-8">
        <Button variant={warning ? 'danger' : 'primary'} size="sm" loading={busy}
          onClick={() => submit(!!warning)}>
          {warning ? 'Всё равно зачесть и выдать сертификат' : 'Зачесть урок'}</Button>
        <Button variant="ghost" size="sm" onClick={onCancel}>Отмена</Button>
      </div>
    </div>
  );
}

function StudentDetailDrawer({ enrollmentId, onClose }) {
  const [nonce, setNonce] = React.useState(0);
  const q = useFakeQuery('academy-student-' + enrollmentId + '-' + nonce,
    () => api.getStudentDetail(enrollmentId), [enrollmentId, nonce]);
  const d = q.data;
  const user = d?.user;
  const toast = useToast();
  // ST-5: сброс попыток теста прямо из карточки ученика — методист приходит
  // сюда, а не в очередь заявок, когда ученик написал ему лично. После сброса
  // перечитываем карточку: прогресс по уроку и статус курса откатываются.
  const { reset, isBusy } = useQuizReset({ onDone: () => setNonce((n) => n + 1) });
  /* Права берём из auth, а НЕ пропсом от вызывающего. Карточка открывается из
     двух мест (вкладка «Ученики» — только методист, и «Аналитика» курса — там
     бывает чистый куратор), и пропс, который забыли прокинуть, — это кнопка,
     дающая 403 по клику. Ровно так и жили действия с сертификатом: они рисуются
     по факту наличия сертификата, а бэк за ними — методистский. */
  const { user: me } = useAuth();
  const canManage = !!me?.academy_manage;
  // Какой урок сейчас в режиме «зачесть вручную» (одна форма за раз: две
  // открытые формы в одном списке — это два разных ответа на один вопрос).
  const [creditId, setCreditId] = React.useState(null);

  const confirm = useConfirm();
  const dropCredit = async (lesson) => {
    /* Одно нажатие откатывало урок, статус курса и стирало причину — ghost-кнопка
       стоит в одном ряду со «Сбросить попытки», на 380px это соседние строки.
       Сброс СДАННОГО теста в той же карточке требует подтверждения; снятие
       зачёта не имеет права быть дешевле. */
    const ok = await confirm({
      title: `Снять ручной зачёт по уроку «${lesson.title}»?`,
      body: 'Урок вернётся в состояние, которое ученик заработал сам; процент курса '
        + 'пересчитается, уроки, открытые этим зачётом, могут снова закрыться. '
        + (lesson.manual_note ? `Причина «${lesson.manual_note}» будет удалена.` : ''),
      confirmLabel: 'Снять зачёт', danger: true });
    if (!ok) return;
    try {
      const res = await api.setLessonCompletion(enrollmentId, lesson.id, { completed: false });
      /* Сертификат снятие НЕ отзывает (это документ о факте) — сказать сейчас,
         а не когда ученик покажет ссылку. */
      toast.show(res && res.certificate_kept
        ? 'Зачёт снят. Выданный сертификат остаётся действительным — отозвать его можно в этой же карточке'
        : 'Зачёт снят', res && res.certificate_kept ? 'warning' : 'muted');
      setNonce((n) => n + 1);
    } catch (e) { toast.show(e?.detail || e?.message || 'Ошибка', 'danger'); }
  };

  return (
    <Drawer open level={2} onClose={onClose} size="md"
      title={user ? user.name : 'Карточка ученика'}
      breadcrumbs={d ? d.course.title : null}>
      {/* PL-15: контейнер container-query — строки уроков считают ширину панели. */}
      <div className="acad-pane">
        <QueryBoundary loading={q.loading && !d} error={q.error} onRetry={q.reload}>
          {d && (
            <div className="stack stack-6">
              {/* ── контакты ученика ── */}
              <section className="acad-section">
                <div className="acad-student-card__head">
                  <Avatar label={user.name} size={44} />
                  <div className="stack stack-1" style={{ minWidth: 0 }}>
                    <span className="acad-student-card__name">{user.name}</span>
                    {user.email && (
                      <a className="acad-student-card__contact" href={`mailto:${user.email}`}>
                        <Icon name="mail" size={13} />{user.email}
                      </a>
                    )}
                    {user.phone && (
                      <a className="acad-student-card__contact" href={`tel:${user.phone}`}>
                        <Icon name="user" size={13} />{user.phone}
                      </a>
                    )}
                  </div>
                </div>
                <div className="acad-student-card__meta">
                  <span><Pill tone={enrStatusMeta(d.enrollment.status).tone} size="sm" glyph={false}>
                    {enrStatusMeta(d.enrollment.status).label}</Pill></span>
                  <span className="t-caption text-mute">Прогресс: <span className="num">{d.enrollment.progress_pct}%</span></span>
                  {d.enrollment.tariff && (
                    <span className="t-caption text-mute">Тариф: {d.enrollment.tariff.title}</span>
                  )}
                  <span className="t-caption text-mute">Записан: {fmt.date(d.enrollment.enrolled_at)}</span>
                  {d.enrollment.expires_at && (
                    <span className="t-caption text-mute">Доступ до: {fmt.date(d.enrollment.expires_at, { timeZone: ACCESS_TZ })}</span>
                  )}
                  {d.enrollment.frozen_at && (
                    <span><Pill tone="warn" size="sm" icon="clock">Заморожен</Pill></span>
                  )}
                </div>
              </section>

              {/* ── сертификат: отзыв и перевыпуск (PL-8) ── */}
              <CertificateSection enrollmentId={enrollmentId} cert={d.certificate}
                canManage={canManage} onChanged={() => setNonce((n) => n + 1)} />

              {/* ── где остановился ── */}
              {d.last_lesson && (
                <section className="acad-section">
                  <div className="acad-section__head"><Icon name="clock" size={15} /><span className="acad-section__title">Остановился на</span></div>
                  <div className="acad-laststop">{d.last_lesson.title}</div>
                </section>
              )}

              {/* ── прогресс по урокам этого курса ── */}
              <section className="acad-section">
                <div className="acad-section__head"><Icon name="check-circle" size={15} /><span className="acad-section__title">Уроки курса</span></div>
                {(d.lessons || []).length === 0
                  ? <span className="t-caption text-mute">В курсе ещё нет уроков.</span>
                  : (
                    <div className="stack stack-2">
                      {d.lessons.map((l) => {
                        const sm = lessonStatusMeta(l.status);
                        const hw = hwStatusMeta(l.homework?.status);
                        return (
                          <React.Fragment key={l.id}>
                          <div className="acad-sd-lesson">
                            <Icon name={sm.icon} size={15} className="acad-sd-lesson__glyph" />
                            <div className="acad-sd-lesson__main">
                              <span className="acad-sd-lesson__title" title={l.title}>{l.title}</span>
                              <span className="acad-sd-lesson__module t-caption text-mute">{l.module_title}{l.is_bonus && ' · бонус'}</span>
                              {/* Причина ручного зачёта — часть статуса, а не деталь
                                  журнала: «Пройден» без объяснения через месяц не
                                  отличить от настоящего прохождения. */}
                              {l.manual && l.manual_note && (
                                <span className="acad-sd-lesson__note t-caption text-mute" title={l.manual_note}>
                                  <Icon name="user" size={12} />
                                  Причина зачёта: {l.manual_note}
                                </span>
                              )}
                              {/* CU-6: причина блокировки человеческим текстом.
                                  Код lock бэк считал и отдавал, а куратор видел
                                  «Не начат» и не понимал, ученик ленится или
                                  урок ему попросту закрыт. */}
                              {l.lock && (
                                <span className="acad-lock">
                                  <Icon name="lock" size={12} />
                                  {api.lockReasonText(l.lock, l.lock_open_at)}
                                </span>
                              )}
                            </div>
                            <div className="acad-sd-lesson__tags">
                              {l.quiz && (
                                <Pill tone={l.quiz.passed ? 'ok' : 'warn'} size="sm" glyph={false}>
                                  Тест {l.quiz.percent}%
                                </Pill>
                              )}
                              {hw && <Pill tone={hw.tone} size="sm" glyph={false}>{hw.label}</Pill>}
                              <Pill tone={sm.tone} size="sm" glyph={false}>{sm.label}</Pill>
                              {/* Отдельная плашка рядом со статусом: административная
                                  отметка не имеет права выглядеть как сданный курс. */}
                              {l.manual && (
                                <span title="Урок засчитан методистом в обход требований">
                                  <Pill tone="accent" size="sm" glyph={false}>Зачтено вручную</Pill>
                                </span>
                              )}
                              {/* Кнопка появляется только когда бэк прислал id теста
                                  (иначе сбрасывать нечего по адресу). Заявки ученика
                                  на пересдачу всегда доступны секцией ниже. */}
                              {l.quiz && l.quiz.id != null && (
                                <Button size="sm" variant="ghost" iconLeft="refresh"
                                  loading={isBusy(enrollmentId, l.quiz.id)}
                                  onClick={() => reset(enrollmentId, l.quiz.id, { lessonTitle: l.title })}
                                  title={`Сбросить попытки теста по уроку «${l.title}»`}>
                                  Сбросить попытки</Button>
                              )}
                              {/* Ручной зачёт — действие МЕТОДИСТА (куратору бэк
                                  ответит 403). Снятие показываем только у своей же
                                  отметки: у честно пройденного урока такая кнопка
                                  обещала бы то, чего не делает — требования она не
                                  отменяет. */}
                              {canManage && (l.manual ? (
                                <Button size="sm" variant="ghost" iconLeft="refresh"
                                  onClick={() => dropCredit(l)}
                                  title={`Снять ручной зачёт по уроку «${l.title}»`}>
                                  Снять зачёт</Button>
                              ) : l.status !== 'completed' && (
                                <Button size="sm" variant="ghost" iconLeft="check-circle"
                                  onClick={() => setCreditId(creditId === l.id ? null : l.id)}
                                  title={`Зачесть урок «${l.title}» вручную`}>
                                  Зачесть вручную</Button>
                              ))}
                            </div>
                          </div>
                          {creditId === l.id && (
                            <LessonCreditForm enrollmentId={enrollmentId} lesson={l}
                              onCancel={() => setCreditId(null)}
                              onDone={() => { setCreditId(null); setNonce((n) => n + 1); }} />
                          )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}
              </section>

              {/* ── заявки этого ученика на пересдачу (ST-5) ── */}
              <RetakeQueue enrollmentId={enrollmentId} courseId={d.course?.id} embedded
                heading="Заявки на пересдачу теста"
                onChanged={() => setNonce((n) => n + 1)} />

              {/* ── другие курсы ученика ── */}
              <section className="acad-section">
                <div className="acad-section__head"><Icon name="layers" size={15} /><span className="acad-section__title">Другие курсы ученика</span></div>
                {(d.other_enrollments || []).length === 0
                  ? <span className="t-caption text-mute">Других записей нет.</span>
                  : (
                    <div className="stack stack-2">
                      {d.other_enrollments.map((oe) => {
                        const sm = enrStatusMeta(oe.status);
                        return (
                          <div key={oe.course.id} className="acad-sd-course">
                            <span className="acad-sd-course__title" title={oe.course.title}>{oe.course.title}</span>
                            <span className="t-caption num text-mute">{oe.progress_pct}%</span>
                            <Pill tone={sm.tone} size="sm" glyph={false}>{sm.label}</Pill>
                          </div>
                        );
                      })}
                    </div>
                  )}
              </section>
            </div>
          )}
        </QueryBoundary>
      </div>
    </Drawer>
  );
}

export { StudentDetailDrawer };
